import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format, addDays } from "date-fns";
import { ArrowRight, Clock, MapPin, Tag } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { departuresBetween, defaultOrigin, originsFor } from "../../lib/departures";
import { duration, placeLabel } from "../../lib/routePages";
import { priceOf, useLivePrices } from "../../lib/livePrices";
import { cityInfo } from "../../lib/cityInfo";
import { formatARS } from "../../lib/site";
import { when, only } from "../home/PromosHome";
import { PriceAlert } from "./PriceAlert";
import { WhatsAppIcon } from "./Icons";
import { api } from "../../lib/api";

// cuenta el interés por destino para el panel (no frena nada si falla)
export const trackDest = (kind, city, origin) => { api.post("/stats/dest-event", { kind, city, origin: origin || null }).catch(() => {}); };

// mismo redondeo que el sistema (a los $100 más cercanos)
const withPct = (price, pct) => (pct ? Math.round((price * (100 - pct)) / 100 / 100) * 100 : price);

const Price = ({ label, value, pct, testid }) => (value == null ? null : (
  <span data-testid={testid}>
    {label} {pct > 0 && <s className="mr-1 text-black/45">{formatARS(value)}</s>}
    <b className={pct > 0 ? "text-emerald-700" : ""}>{formatARS(withPct(value, pct))}</b>
  </span>
));

const Departure = ({ d, origin, dest, prices, pct }) => {
  const semi = priceOf(prices, origin, dest, 0);
  const cama = priceOf(prices, origin, dest, 1);
  return (
    <li data-testid={`dest-departure-${d.id}`} className="rounded-xl border border-black/10 p-4">
      <div className="flex items-center gap-3 font-display text-xl">
        <span>{d.from[1]}</span><ArrowRight className="h-4 w-4 text-black/40" /><span>{d.to[1]}</span>
        <span className="ml-auto flex items-center gap-1 font-sans text-xs text-black/60"><Clock className="h-3.5 w-3.5" />{duration(d.minutes)}</span>
      </div>
      <p className="mt-1 text-xs text-black/60">{d.days} · sube en {d.from[0]}</p>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
        <Price label="Semicama" value={semi} pct={pct} testid={`dest-price-semicama-${d.id}`} />
        <Price label="Cama" value={cama} pct={pct} testid={`dest-price-cama-${d.id}`} />
        {semi == null && cama == null && <span className="text-black/60">Consultá la tarifa</span>}
      </div>
    </li>
  );
};

// Mensaje listo para WhatsApp con las salidas, precios (con promo si hay) y el link para comprar
const shareUrl = ({ origin, city, deps, prices, promo, buy }) => {
  const pct = promo?.discount_pct || 0;
  const money = (cat) => { const v = priceOf(prices, origin, city, cat); return v == null ? null : formatARS(withPct(v, pct)); };
  const fares = [money(0) && `Semicama ${money(0)}`, money(1) && `Cama ${money(1)}`].filter(Boolean).join(" / ");
  const lines = deps.map((d) => `• ${d.days}: sale ${d.from[1]} y llega ${d.to[1]} (${duration(d.minutes).replace(/\u00a0/g, " ")})`);
  const text = [`Santiagueño: ${placeLabel(origin)} → ${placeLabel(city)}`, ...lines, fares && `Precio por pasajero: ${fares}${pct ? ` (PROMO -${pct}%)` : ""}`,
    `Mirá las fechas y comprá: ${window.location.origin}${buy}`].filter(Boolean).join("\n");
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
};

const PromoBanner = ({ p }) => (
  <div data-testid="destination-promo" className="flex items-start gap-3 rounded-xl bg-[var(--lime)]/25 p-3 ring-1 ring-[var(--lime)]">
    <span data-testid="destination-promo-badge" className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[var(--ink)] px-2.5 py-1 text-xs font-bold text-white"><Tag className="h-3 w-3" /> PROMO -{p.discount_pct}%</span>
    <p className="text-xs text-black/75">{when(p)} · {only(p)}{p.max_seats ? ` · cupos limitados` : ""}. Precio con descuento en las fechas de la promo.</p>
  </div>
);

const CityHero = ({ city }) => {
  const info = cityInfo(city);
  return (
    <div className="relative -mx-6 -mt-6 mb-1 h-40 overflow-hidden sm:rounded-t-lg">
      <img data-testid="destination-photo" src={info.img} alt={city} className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
      <p data-testid="destination-description" className="absolute inset-x-6 bottom-3 text-sm text-white">{info.text}</p>
    </div>
  );
};

/** Ventanita con foto, descripción, promos, salidas y precios hacia un destino de la franja. */
export const DestinationDialog = ({ city, onClose, promos }) => {
  const prices = useLivePrices();
  const [origin, setOrigin] = useState("");
  const origins = useMemo(() => (city ? originsFor(city) : []), [city]);
  useEffect(() => {
    if (!city) return;
    const promoFrom = promos.find((p) => p.destination === city && origins.includes(p.origin));
    setOrigin(promoFrom ? promoFrom.origin : defaultOrigin(city));
  }, [city, promos, origins]);
  const promo = promos.filter((p) => p.origin === origin && p.destination === city).sort((a, b) => b.discount_pct - a.discount_pct)[0];
  const deps = useMemo(() => (city && origin ? departuresBetween(origin, city) : []), [city, origin]);
  const tomorrow = format(addDays(new Date(), 1), "yyyy-MM-dd");
  const f = promo && promo.date_from > tomorrow ? promo.date_from : tomorrow;
  const buy = `/pasajes?${new URLSearchParams({ o: origin, d: city || "", f, p: "1" })}`;
  return (
    <Dialog open={!!city} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="destination-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-lg [&>button]:z-10 [&>button]:rounded-full [&>button]:bg-white [&>button]:p-1 [&>button]:opacity-100">
        {city && <CityHero city={city} />}
        <DialogHeader>
          <DialogTitle data-testid="destination-dialog-title" className="font-display text-3xl">{city && placeLabel(city)}</DialogTitle>
          <DialogDescription>Salidas y precios por pasajero</DialogDescription>
        </DialogHeader>
        {origins.length > 0 ? (
          <>
            <label className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4" /> Desde
              <select data-testid="destination-origin-select" value={origin} onChange={(e) => setOrigin(e.target.value)} className="flex-1 rounded-lg border border-black/15 bg-white px-3 py-2">
                {origins.map((c) => <option key={c} value={c}>{placeLabel(c)}</option>)}
              </select>
            </label>
            {promo ? <PromoBanner p={promo} /> : origin && <PriceAlert key={origin} o={origin} d={city} testid="destination-alert" />}
            <ul className="space-y-3">{deps.map((d) => <Departure key={d.id} d={d} origin={origin} dest={city} prices={prices} pct={promo?.discount_pct || 0} />)}</ul>
            <Link data-testid="destination-buy-link" to={buy} onClick={() => { trackDest("buy", city, origin); onClose(); }} className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--ink)] px-6 py-3 font-semibold text-white transition-transform hover:-translate-y-0.5">
              {promo ? "Ver fechas en promo y comprar" : "Ver fechas y comprar"} <ArrowRight className="h-4 w-4" />
            </Link>
            <a data-testid="destination-share-whatsapp" href={shareUrl({ origin, city, deps, prices, promo, buy })} target="_blank" rel="noopener noreferrer" onClick={() => trackDest("share", city, origin)}
              className="-mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-semibold text-[var(--ink)] transition-transform hover:-translate-y-0.5">
              <WhatsAppIcon className="h-4 w-4" /> Compartir por WhatsApp
            </a>
          </>
        ) : <p data-testid="destination-no-departures" className="text-sm text-black/60">Este punto es origen de nuestros viajes. Consultanos por las salidas.</p>}
      </DialogContent>
    </Dialog>
  );
};
